import { getBlockchainRpc } from "@talentir/shared/helpers";
import { ChainNotConfiguredError, createConnector } from "@wagmi/core";
import { type JsonRpcError, Magic, RPCErrorCode } from "magic-sdk";
import { createSmartAccountClient } from "permissionless";
import { toEcdsaKernelSmartAccount } from "permissionless/accounts";
import { createPimlicoClient } from "permissionless/clients/pimlico";
import { http, type Chain, type EIP1193Provider, SwitchChainError, createPublicClient, getAddress, toHex } from "viem";
import { entryPoint06Address } from "viem/account-abstraction";
import { normalizeChainId } from "wagmi";
import { getKernelVersion } from "./get-kernel-version";
import type { MagicConnectorAdditions } from "./magic-connectors";

type ExcludeUndefined<T> = T extends undefined ? never : T;

export type MagicAAConnectorAdditions = {
    getOwner: () => Promise<string>;
};

export function magicConnectorAa() {
    let magic:
        | { sdk: Magic; chainId: number; kernelClient?: Awaited<ReturnType<typeof providerToKernelSmartAccountClient>> }
        | undefined;
    type Provider = ExcludeUndefined<typeof magic>["sdk"]["rpcProvider"];

    return createConnector<Provider, MagicConnectorAdditions & MagicAAConnectorAdditions>((config) => {
        const getMagicSDK = async (chainId?: number) => {
            if (magic == null || magic.kernelClient == null || (chainId != null && magic.chainId !== chainId)) {
                const chain =
                    config.chains.find((x) => x.id === chainId) ??
                    config.chains.find((x) => x.id === magic?.chainId) ??
                    config.chains[0];
                if (chain == null) throw new Error("Chain not supported");

                const magicSdk = new Magic("pk_live_5AB0A9D428370044", {
                    network: {
                        rpcUrl: getBlockchainRpc(chain),
                        chainId: chain.id,
                    },
                });

                const kernelClient = (await magicSdk.user.isLoggedIn())
                    ? await providerToKernelSmartAccountClient(magicSdk.rpcProvider, chain)
                    : undefined;

                magic = { sdk: magicSdk, chainId: chain.id, kernelClient };
            }

            return magic;
        };

        let onOtpEnterReady: ((submitToken: (token: string) => void, isRetry: boolean) => void) | undefined;
        let email = "";

        return {
            id: "magic-aa",
            name: "Magic Wallet AA",
            type: "magicWalletAa" as const,
            async connect({ chainId } = {}) {
                try {
                    const magic = await getMagicSDK(chainId);
                    const provider = magic.sdk.rpcProvider;

                    provider.on("disconnect", () => {
                        this.onDisconnect();
                    });

                    // Check if there is a user logged in
                    const isAuthenticated = await this.isAuthorized();

                    // if there is a user logged in, return the user
                    if (isAuthenticated) {
                        let currentChainId = magic.chainId;
                        if (chainId != null && currentChainId !== chainId) {
                            const chain = await this.switchChain?.({ chainId });
                            currentChainId = chain?.id ?? currentChainId;
                        }

                        return {
                            accounts: await this.getAccounts(),
                            chainId: currentChainId,
                        };
                    }

                    // open the modal and process the magic login steps
                    const localMagic = await getMagicSDK(chainId);

                    const handle = localMagic.sdk.auth.loginWithEmailOTP({
                        email,
                        showUI: false,
                    });

                    await new Promise((resolve) => {
                        void handle.on("email-otp-sent", () => {
                            if (onOtpEnterReady == null) {
                                throw new Error("onOtpEnterReady function is not set");
                            }

                            onOtpEnterReady((token) => {
                                handle.emit("verify-email-otp", token);
                                resolve(undefined);
                            }, false);
                        });
                    });

                    await new Promise((resolve, reject) => {
                        try {
                            void handle
                                .on("done", () => {
                                    resolve(undefined);
                                })
                                .on("invalid-email-otp", () => {
                                    // When user enters invalid OTP, we need to ask them to enter it again
                                    if (onOtpEnterReady == null) {
                                        throw new Error("onOtpEnterReady function is not set");
                                    }

                                    onOtpEnterReady((token) => {
                                        handle.emit("verify-email-otp", token);
                                    }, true);
                                })
                                .on("error", (reason) => {
                                    reject(new Error(reason as string));
                                });
                        } catch (e) {
                            reject(e);
                        }
                    });

                    return {
                        accounts: await this.getAccounts(),
                        chainId: chainId ?? 0,
                    };
                } catch (e) {
                    const magicError = e as JsonRpcError;
                    if (magicError.code === RPCErrorCode.InvalidParams) {
                        throw new Error("Email is not valid");
                    }

                    throw new Error(magicError.message);
                } finally {
                    onOtpEnterReady = undefined;
                }
            },
            async disconnect() {
                if (magic == null) return;

                await magic.sdk.user.logout();
                magic = undefined;
            },
            async getAccounts() {
                const client = await this.getClient?.();
                const address = client?.account?.address;

                if (address == null) return [];

                return [getAddress(address)];
            },
            async getChainId() {
                const provider = (await getMagicSDK()).sdk.rpcProvider;
                const chainId = await provider.request<number>({ method: "eth_chainId" });
                return normalizeChainId(chainId);
            },
            async getProvider() {
                const magic = await getMagicSDK();
                return magic.sdk.rpcProvider;
            },
            async isAuthorized() {
                try {
                    if (!(await (await getMagicSDK()).sdk.user.isLoggedIn())) {
                        throw new Error();
                    }

                    const account = await this.getAccounts();
                    return account.length !== 0;
                } catch {
                    return false;
                }
            },
            async switchChain({ chainId }) {
                const chain = config.chains.find((x) => x.id === chainId);

                if (chain == null) throw new SwitchChainError(new ChainNotConfiguredError());

                await getMagicSDK(chainId);
                this.onChainChanged(toHex(chainId));

                return chain;
            },
            onAccountsChanged(accounts) {
                if (accounts.length === 0) {
                    config.emitter.emit("disconnect");
                } else {
                    config.emitter.emit("change", {
                        accounts: accounts.map((x) => getAddress(x)),
                    });
                }
            },
            onChainChanged(chain) {
                const chainId = normalizeChainId(chain);
                config.emitter.emit("change", { chainId });
            },
            onDisconnect() {
                config.emitter.emit("disconnect");
                getMagicSDK().then((magic) => {
                    magic.sdk.rpcProvider.removeListener("disconnect", this.onDisconnect.bind(this));
                });
            },

            async getClient(data) {
                const client = (await getMagicSDK(data?.chainId)).kernelClient;

                if (client == null) {
                    throw new Error("Client not found");
                }

                return client;
            },
            setOnOtpEnterReady: (
                onOtpEnterReadyFn: (submitToken: (token: string) => void, isRetry: boolean) => void,
            ) => {
                onOtpEnterReady = onOtpEnterReadyFn;
            },
            setEmail: (newEmail: string) => {
                email = newEmail;
            },
            async getOwner() {
                const provider = (await getMagicSDK()).sdk.rpcProvider;
                const accounts = (
                    await provider.request<string[]>({
                        method: "eth_accounts",
                    })
                ).map((x) => getAddress(x));

                return accounts[0] ?? "0x00";
            },
        };
    });
}

async function providerToKernelSmartAccountClient(provider: EIP1193Provider, chain: Chain) {
    const pimlicoUrl = `https://api.pimlico.io/v2/${chain.id}/rpc?apikey=pim_E9uAR5RZBEXZ5Lm6i2YKGB`;

    const rpc = getBlockchainRpc(chain);

    const publicClient = createPublicClient({
        chain,
        transport: http(rpc),
    });

    const pimlicoClient = createPimlicoClient({
        transport: http(pimlicoUrl),
        entryPoint: {
            address: entryPoint06Address,
            version: "0.6",
        },
    });

    const actualKernelVersion = await getKernelVersion({
        owners: [provider],
        client: publicClient,
        version: "0.2.4",
        entryPoint: {
            address: entryPoint06Address,
            version: "0.6",
        },
    });

    const simpleSmartAccount = await toEcdsaKernelSmartAccount({
        owners: [provider],
        client: publicClient,
        version: actualKernelVersion,
        entryPoint: {
            address: entryPoint06Address,
            version: "0.6",
        },
    });

    const smartAccountClient = createSmartAccountClient({
        account: simpleSmartAccount,
        chain,
        bundlerTransport: http(pimlicoUrl),
        paymaster: pimlicoClient,
        userOperation: {
            estimateFeesPerGas: async () => {
                return (await pimlicoClient.getUserOperationGasPrice()).fast;
            },
        },
    });

    return smartAccountClient;
}
